import React from 'react';
import { Star } from 'react-feather';
import type { LabelProps } from './types';
import { useLabel } from './useLabel';

// ============================================================================
// Label Component - Horizontal Navigation Item with Left/Right Icons
// ============================================================================

const Label: React.FC<LabelProps> = ({
  children,
  size = 'Medium',
  labelType = 'Round',
  state = 'Enabled',
  selected = false,
  disabled = false,
  leftIcon,
  rightIcon,
  showLeftIcon = true,
  showRightIcon = true,
  showLabel = true,
  isIconOnly = false,
  onClick,
  className = '',
  ...props
}) => {
  const {
    sizeStyles,
    stateStyles,
    borderRadius,
    isDisabled,
    computedState,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
    handleFocus,
    handleBlur,
  } = useLabel({
    size,
    labelType,
    state,
    selected,
    disabled,
  });

  // Default left icon if none provided
  const defaultLeftIcon = (
    <Star size={sizeStyles.iconSize} color={stateStyles.iconColor} />
  );

  // When isIconOnly is true, only show left icon
  const shouldShowLeftIcon =
    isIconOnly ||
    (showLeftIcon && (leftIcon !== undefined || leftIcon === undefined));
  const shouldShowLabel = !isIconOnly && showLabel;
  const shouldShowRightIcon =
    !isIconOnly && showRightIcon && rightIcon !== undefined;

  // Separate style from other props to prevent override
  const { style: _, ...restProps } = props;

  // Merge props.style with component styles, ensuring backgroundColor is preserved
  const mergedStyle = {
    // When isIconOnly, shrink to fit the icon with minimal padding
    height: isIconOnly ? sizeStyles.iconSize + 16 : sizeStyles.height,
    width: isIconOnly ? sizeStyles.iconSize + 16 : 'auto',
    paddingLeft: isIconOnly ? 8 : sizeStyles.paddingX,
    paddingRight: isIconOnly ? 8 : sizeStyles.paddingX,
    paddingTop: isIconOnly ? 8 : sizeStyles.paddingY,
    paddingBottom: isIconOnly ? 8 : sizeStyles.paddingY,
    gap: sizeStyles.gap,
    borderRadius,
    backgroundColor: stateStyles.backgroundColor,
    color: stateStyles.textColor,
    cursor: stateStyles.cursor,
    boxShadow: 'none',
    fontFamily: 'Roboto, system-ui, Avenir, Helvetica, Arial, sans-serif',
    minWidth: shouldShowRightIcon ? '120px' : 'auto',
    // Smooth transition for isIconOnly changes
    transition:
      'width 300ms ease-in-out, height 300ms ease-in-out, padding 300ms ease-in-out, background-color 200ms ease, color 200ms ease',
    ...props.style,
  };

  return (
    <button
      type="button"
      disabled={isDisabled}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onFocus={handleFocus}
      onBlur={handleBlur}
      data-testid={children?.toString()}
      aria-label={children?.toString()}
      data-state={computedState}
      data-selected={selected}
      data-icon-only={isIconOnly}
      className={`
        relative
        inline-flex
        flex-row
        items-center
        outline-none
        overflow-hidden
        ${shouldShowRightIcon ? 'justify-between' : isIconOnly ? 'justify-center' : 'justify-start'}
        ${className}
      `}
      style={mergedStyle}
      {...restProps}
    >
      {/* Left section: Left Icon + Label Text */}
      <div
        className="inline-flex flex-row items-center"
        style={{ gap: shouldShowLabel ? sizeStyles.gap : 0 }}
      >
        {/* Left Icon */}
        {shouldShowLeftIcon && (
          <span
            className="flex items-center justify-center shrink-0"
            style={{
              width: sizeStyles.iconSize,
              height: sizeStyles.iconSize,
              color: stateStyles.iconColor,
            }}
          >
            {leftIcon || defaultLeftIcon}
          </span>
        )}

        {/* Label Text - with fade animation */}
        <span
          style={{
            fontSize: sizeStyles.fontSize,
            lineHeight: `${sizeStyles.lineHeight}px`,
            letterSpacing: sizeStyles.letterSpacing,
            fontWeight: 500,
            color: stateStyles.textColor,
            whiteSpace: 'nowrap',
            opacity: shouldShowLabel ? 1 : 0,
            width: shouldShowLabel ? 'auto' : 0,
            overflow: 'hidden',
            transition: 'opacity 200ms ease-in-out, width 300ms ease-in-out',
          }}
        >
          {children}
        </span>
      </div>

      {/* Right Icon - positioned at far right */}
      {shouldShowRightIcon && (
        <span
          className="flex items-center justify-center shrink-0"
          style={{
            width: sizeStyles.iconSize,
            height: sizeStyles.iconSize,
            color: stateStyles.iconColor,
          }}
        >
          {rightIcon}
        </span>
      )}
    </button>
  );
};

export default Label;
